import { useEffect, useMemo, useRef, useState } from 'react';
import type { SpeciesId } from '../../types.ts';
import { filterChampionsNatures, NATURE_LABEL } from '../../util/champions-labels.ts';
import {
  evHeadroom,
  finalStats,
  NATURE_LIST,
  natureGlyph,
  type Stat,
  type StatBlock,
  type StatSystem,
  snapEv,
  totalEvs,
} from '../../util/stat-math.ts';

// Per-regulation EV/SP budget. Keeps the Editor a pure component (no async
// regulation fetch). When we add Reg M-B or a mainline format, extend this
// table — the per-stat / total / step / system fields drive every input
// constraint and label below.
const REGULATION_BUDGET: Record<string, { system: StatSystem; perStat: number; total: number; step: number; unit: string }> = {
  'M-A': { system: 'sp', perStat: 32, total: 66, step: 1, unit: 'SP' },
};
const DEFAULT_BUDGET = {
  system: 'ev' as StatSystem,
  perStat: 252,
  total: 510,
  step: 4,
  unit: 'EVs',
} as const;
import { useIsMobile } from '../../util/useIsMobile.ts';
import { Sprite } from '../Sprite.tsx';
import { TypeChip } from '../TypeChip.tsx';
import {
  type BuilderSpecies,
  type BuilderSpeciesOptions,
  getItemList,
  getSpeciesCatalog,
  getSpeciesOptions,
} from './builder-data.ts';
import { PickerField } from './PickerField.tsx';
import type { EditorMember } from './serialize.ts';

// EditorMember is defined in serialize.ts (a .ts file) so non-tsx modules
// can import it without --jsx config drift. Re-exported here for callers
// that already import from Editor.tsx.
export type { EditorMember } from './serialize.ts';

const STATS: { id: Stat; label: string }[] = [
  { id: 'hp', label: 'HP' },
  { id: 'atk', label: 'Atk' },
  { id: 'def', label: 'Def' },
  { id: 'spa', label: 'SpA' },
  { id: 'spd', label: 'SpD' },
  { id: 'spe', label: 'Spe' },
];

// One Editor edits one slot. Species swap happens via the "change" link in
// the header; the parent supplies onChangeSpecies for the species picker —
// kept outside the Editor so the picker overlay can be shared with the
// "empty slot" / "add to team" flows on the standalone page.
export function Editor({
  member,
  onChange,
  onChangeSpecies,
  onClear,
  slotLabel,
  regulationId = 'M-A',
}: {
  member: EditorMember;
  onChange: (next: EditorMember) => void;
  onChangeSpecies?: () => void;
  onClear?: () => void;
  slotLabel?: string;
  regulationId?: string;
}) {
  const [catalog, setCatalog] = useState<BuilderSpecies[] | null>(null);
  const [opts, setOpts] = useState<BuilderSpeciesOptions | null>(null);
  const [itemList, setItemList] = useState<string[] | null>(null);
  const [loadingErr, setLoadingErr] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const budget = REGULATION_BUDGET[regulationId] ?? DEFAULT_BUDGET;

  // Load catalog + item list once per page.
  useEffect(() => {
    let cancelled = false;
    Promise.all([getSpeciesCatalog(regulationId), getItemList(regulationId)])
      .then(([c, i]) => {
        if (cancelled) return;
        setCatalog(c);
        setItemList(i);
      })
      .catch((e) => !cancelled && setLoadingErr(String(e?.message ?? e)));
    return () => {
      cancelled = true;
    };
  }, [regulationId]);

  // Reload per-species options whenever the species changes.
  useEffect(() => {
    if (!member.species) {
      setOpts(null);
      return;
    }
    let cancelled = false;
    getSpeciesOptions(member.species, regulationId)
      .then((o) => !cancelled && setOpts(o))
      .catch((e) => !cancelled && setLoadingErr(String(e?.message ?? e)));
    return () => {
      cancelled = true;
    };
  }, [member.species, regulationId]);

  const species = useMemo(
    () => catalog?.find((s) => s.id === member.species) ?? null,
    [catalog, member.species],
  );

  // Final stats for the right-hand column of the EV editor. Recompute every
  // render — finalStats is cheap and the alternative (memoising on each
  // sub-stat) is more code than it saves. The `system` flips between SV's
  // /252-EV math and Champions's PS-formula SP math at the regulation
  // boundary.
  const finals = useMemo<StatBlock | null>(() => {
    if (!species) return null;
    return finalStats({
      base: species.base_stats,
      evs: member.evs,
      ivs: member.ivs,
      nature: member.nature,
      system: budget.system,
    });
  }, [species, member.evs, member.ivs, member.nature, budget.system]);

  const updateField = <K extends keyof EditorMember>(key: K, value: EditorMember[K]) => {
    onChange({ ...member, [key]: value });
  };

  const updateEv = (stat: Stat, raw: number) => {
    const snapped = snapEv(raw, budget.system);
    const headroom = evHeadroom(stat, member.evs, budget.total, budget.perStat);
    const next = Math.min(snapped, headroom);
    onChange({ ...member, evs: { ...member.evs, [stat]: next } });
  };

  const updateMove = (slot: 0 | 1 | 2 | 3, moveId: string) => {
    const moves = [...(member.moves ?? ['', '', '', ''])];
    while (moves.length < 4) moves.push('');
    moves[slot] = moveId;
    onChange({ ...member, moves });
  };

  if (loadingErr) {
    return (
      <div className="vb-card" style={{ padding: 14, color: 'var(--neg)', fontSize: 12 }}>
        Builder data failed to load: {loadingErr}
      </div>
    );
  }
  if (!catalog || !species) {
    return (
      <div className="vb-card" style={{ padding: 14, color: 'var(--fg-4)', fontSize: 12 }}>
        loading…
      </div>
    );
  }

  const evTotal = totalEvs(member.evs);

  const applyTopSet = (set: import('./builder-data.ts').BuilderTopSet) => {
    // Top sets carry item/ability/moves explicitly + the form's modal
    // nature/spread. We merge into the current member rather than replacing
    // wholesale — preserving species, IVs, and any UI-only fields.
    const normalizedItem = set.item ? set.item.toLowerCase().replace(/[^a-z0-9]/g, '') : undefined;
    const evs = parseSpread(set.modal_ev_spread) ?? member.evs ?? {};
    onChange({
      ...member,
      item: normalizedItem ?? member.item,
      ability: set.ability ?? member.ability,
      nature: set.modal_nature ?? member.nature,
      evs,
      moves: padMoves(set.moves),
    });
  };

  return (
    <div className="vb-card" style={{ overflow: 'visible' }}>
      <EditorHeader
        species={species}
        member={member}
        slotLabel={slotLabel}
        onChangeSpecies={onChangeSpecies}
        onClear={onClear}
        topSets={opts?.top_sets ?? []}
        onApplySet={applyTopSet}
      />
      {/* Item / Ability / Nature row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: 10,
          padding: '12px 14px',
        }}
      >
        <PickerField
          label="item"
          value={member.item}
          onChange={(v) => updateField('item', v)}
          options={(itemList ?? []).map((id) => ({ id, label: id }))}
          placeholder="no item"
        />
        <PickerField
          label="ability"
          value={member.ability}
          onChange={(v) => updateField('ability', v)}
          options={(opts?.legal_abilities ?? species.abilities).map((id) => ({
            id,
            label: id,
          }))}
          placeholder="—"
        />
        <PickerField
          label={NATURE_LABEL.short}
          value={member.nature}
          onChange={(v) => updateField('nature', v)}
          options={filterChampionsNatures(NATURE_LIST).map((id) => ({
            id,
            label: id,
            render: <NatureRow nature={id} />,
          }))}
          placeholder="Serious"
        />
      </div>

      {/* Static info strip */}
      <div
        style={{
          display: 'flex',
          gap: 18,
          padding: '8px 14px',
          background: 'var(--bg-2)',
          borderTop: '1px solid var(--line-1)',
          borderBottom: '1px solid var(--line-1)',
          fontSize: 10.5,
          color: 'var(--fg-3)',
        }}
        className="mono"
      >
        <span>
          <span className="caps" style={{ color: 'var(--fg-4)', marginRight: 4 }}>
            level
          </span>
          50
        </span>
        <span>
          <span className="caps" style={{ color: 'var(--fg-4)', marginRight: 4 }}>
            format
          </span>
          Reg {regulationId}
        </span>
        <span
          style={{
            color:
              evTotal > budget.total
                ? 'var(--neg)'
                : evTotal < budget.total - 2
                  ? 'var(--warn)'
                  : 'var(--fg-3)',
          }}
        >
          <span className="caps" style={{ color: 'var(--fg-4)', marginRight: 4 }}>
            {budget.unit}
          </span>
          {evTotal}/{budget.total}
        </span>
      </div>

      {/* EV editor */}
      <div style={{ padding: '10px 14px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 6,
          }}
        >
          <span
            className="caps mono"
            style={{ fontSize: 9.5, color: 'var(--fg-4)', letterSpacing: '0.08em' }}
          >
            {budget.unit === 'SP' ? 'stat points' : 'EV spread'}
          </span>
          <button
            type="button"
            onClick={() =>
              onChange({
                ...member,
                evs: { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 },
              })
            }
            className="caps mono"
            style={{
              fontSize: 9,
              color: 'var(--fg-4)',
              background: 'transparent',
              border: '1px solid var(--line-1)',
              borderRadius: 2,
              padding: '2px 6px',
              cursor: 'pointer',
            }}
          >
            clear
          </button>
        </div>
        {STATS.map((s) => (
          <EvRow
            key={s.id}
            stat={s.id}
            label={s.label}
            base={species.base_stats[s.id]}
            ev={member.evs?.[s.id] ?? 0}
            iv={member.ivs?.[s.id] ?? 31}
            final={finals?.[s.id] ?? 0}
            natureGlyph={natureGlyph(member.nature, s.id)}
            onChange={(v) => updateEv(s.id, v)}
            compact={isMobile}
            perStatCap={budget.perStat}
            step={budget.step}
          />
        ))}
      </div>

      {/* Moves */}
      <div style={{ padding: '8px 14px 14px' }}>
        <span
          className="caps mono"
          style={{
            fontSize: 9.5,
            color: 'var(--fg-4)',
            letterSpacing: '0.08em',
            display: 'block',
            marginBottom: 6,
          }}
        >
          moveset
        </span>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {[0, 1, 2, 3].map((i) => (
            <PickerField
              key={i}
              value={member.moves?.[i] || undefined}
              onChange={(v) => updateMove(i as 0 | 1 | 2 | 3, v)}
              options={(opts?.seen_moves ?? []).map((m) => ({ id: m, label: m }))}
              placeholder={`move ${i + 1}`}
              emptyText={opts ? 'no moves in corpus for this species' : 'loading…'}
            />
          ))}
        </div>
        {opts && opts.seen_moves.length > 0 && (
          <div
            className="mono"
            style={{
              fontSize: 9.5,
              color: 'var(--fg-4)',
              marginTop: 6,
            }}
          >
            {opts.seen_moves.length} moves observed in tournament corpus — picker filters to what's
            actually been used.
          </div>
        )}
      </div>
    </div>
  );
}

// Parse a Pikalytics modal spread string like "2/32/0/0/0/32" (Champions
// /32 point-buy format — total ≤66, each ≤32) into a mainline /252 EV map.
// Mirrors src/calc/dashboard-helpers.ts parseSpreadToEvs but inlined here
// so the frontend doesn't pull the server module.
function parseSpread(spread: string | null | undefined): Partial<StatBlock> | undefined {
  if (typeof spread !== 'string') return undefined;
  const parts = spread.split('/').map((s) => Number.parseInt(s.trim(), 10));
  if (parts.length !== 6 || parts.some((n) => !Number.isFinite(n))) return undefined;
  const total = parts.reduce((s, n) => s + n, 0);
  const looksChampions = total <= 66 && parts.every((n) => n <= 32);
  const scale = (n: number) =>
    Math.max(0, Math.min(252, looksChampions ? Math.round((n / 32) * 252) : n));
  return {
    hp: scale(parts[0]),
    atk: scale(parts[1]),
    def: scale(parts[2]),
    spa: scale(parts[3]),
    spd: scale(parts[4]),
    spe: scale(parts[5]),
  };
}

function padMoves(moves: readonly string[]): string[] {
  const out = [...moves.slice(0, 4)];
  while (out.length < 4) out.push('');
  return out;
}

function EditorHeader({
  species,
  member,
  slotLabel,
  onChangeSpecies,
  onClear,
  topSets,
  onApplySet,
}: {
  species: BuilderSpecies;
  member: EditorMember;
  slotLabel?: string;
  onChangeSpecies?: () => void;
  onClear?: () => void;
  topSets: import('./builder-data.ts').BuilderTopSet[];
  onApplySet: (set: import('./builder-data.ts').BuilderTopSet) => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        borderBottom: '1px solid var(--line-1)',
      }}
    >
      <Sprite species={species.id as SpeciesId} size={48} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--fg-1)' }}>
            {species.name}
          </span>
          {onChangeSpecies && (
            <button
              type="button"
              onClick={onChangeSpecies}
              className="caps mono"
              style={{
                fontSize: 9,
                color: 'var(--accent-2)',
                background: 'var(--accent-bg)',
                border: '1px solid var(--accent-line)',
                borderRadius: 2,
                padding: '1px 6px',
                letterSpacing: '0.06em',
                cursor: 'pointer',
              }}
            >
              change
            </button>
          )}
          {slotLabel && (
            <span
              className="caps mono"
              style={{ fontSize: 9, color: 'var(--fg-4)', letterSpacing: '0.06em' }}
            >
              · {slotLabel}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 3, marginTop: 4 }}>
          {species.types.map((t) => (
            <TypeChip key={t} type={t as any} size="sm" />
          ))}
        </div>
      </div>
      {topSets.length > 0 && <LoadSetButton topSets={topSets} onApply={onApplySet} />}
      {onClear && (
        <button
          type="button"
          onClick={onClear}
          className="caps mono"
          style={{
            fontSize: 10,
            color: 'var(--fg-4)',
            background: 'transparent',
            border: '1px solid var(--line-1)',
            borderRadius: 2,
            padding: '2px 7px',
            cursor: 'pointer',
          }}
          title="Clear this slot"
        >
          ×
        </button>
      )}
    </div>
  );
}

// "Load top set" dropdown button. Opens a small popover listing the
// species' Pikalytics-featured tournament sets (player + record + item +
// ability + moves). Click a set to apply it to the current member.
function LoadSetButton({
  topSets,
  onApply,
}: {
  topSets: import('./builder-data.ts').BuilderTopSet[];
  onApply: (set: import('./builder-data.ts').BuilderTopSet) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="caps mono"
        style={{
          fontSize: 9,
          color: 'var(--accent-2)',
          background: 'var(--accent-bg)',
          border: '1px solid var(--accent-line)',
          borderRadius: 2,
          padding: '2px 7px',
          letterSpacing: '0.06em',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
        title="Apply a Pikalytics-featured tournament set"
      >
        load set ({topSets.length})
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 4,
            width: 320,
            maxHeight: 380,
            overflowY: 'auto',
            background: 'var(--bg-1)',
            border: '1px solid var(--line-2)',
            borderRadius: 3,
            zIndex: 30,
            boxShadow: '0 4px 18px rgba(0,0,0,0.35)',
          }}
        >
          {topSets.map((s, i) => (
            <button
              // biome-ignore lint/suspicious/noArrayIndexKey: sets are positional in the featured_teams array; no stable id
              key={`${s.player}-${i}`}
              type="button"
              onClick={() => {
                onApply(s);
                setOpen(false);
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                width: '100%',
                padding: '8px 12px',
                background: 'transparent',
                border: 'none',
                borderBottom: '1px solid var(--line-1)',
                textAlign: 'left',
                cursor: 'pointer',
                color: 'var(--fg-1)',
                gap: 2,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <span style={{ fontSize: 12, fontWeight: 500 }}>{s.player}</span>
                <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>
                  {s.record}
                </span>
              </div>
              <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>
                {s.form} · {s.item ?? '—'} · {s.ability ?? '—'}
              </span>
              <span
                className="mono"
                style={{ fontSize: 10, color: 'var(--fg-4)', wordBreak: 'break-word' }}
              >
                {s.moves.join(' / ')}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function EvRow({
  stat,
  label,
  base,
  ev,
  iv,
  final,
  natureGlyph,
  onChange,
  compact = false,
  perStatCap = 252,
  step = 4,
}: {
  stat: Stat;
  label: string;
  base: number;
  ev: number;
  iv: number;
  final: number;
  natureGlyph: '▲' | '▼' | null;
  onChange: (next: number) => void;
  compact?: boolean;
  perStatCap?: number;
  step?: number;
}) {
  // Shared bits used by both layouts.
  const labelEl = (
    <span
      className="caps mono"
      style={{ fontSize: 10, color: 'var(--fg-2)', letterSpacing: '0.06em' }}
      title={`base ${base}`}
    >
      {label}
    </span>
  );
  const numberInput = (
    <input
      type="number"
      // inputMode=numeric brings up the numeric keypad on mobile without
      // requiring the spinner UI. font-size 16 suppresses iOS auto-zoom.
      inputMode="numeric"
      min={0}
      max={perStatCap}
      step={step}
      value={ev}
      onChange={(e) => onChange(Number.parseInt(e.target.value, 10) || 0)}
      className="mono tabular"
      style={{
        fontSize: compact ? 16 : 11,
        padding: '3px 5px',
        background: 'var(--bg-2)',
        color: 'var(--fg-1)',
        border: '1px solid var(--line-1)',
        borderRadius: 2,
        textAlign: 'right',
        width: compact ? 64 : 50,
      }}
    />
  );
  const finalEl = (
    <span
      className="mono tabular"
      style={{
        fontSize: 11,
        color: 'var(--fg-1)',
        fontWeight: 600,
        textAlign: 'right',
        whiteSpace: 'nowrap',
      }}
      title={`Final stat at lvl 50 with ${ev} ${perStatCap === 32 ? 'SP' : 'EVs'} / ${iv} IVs`}
    >
      {natureGlyph && (
        <span
          style={{
            color: natureGlyph === '▲' ? 'var(--pos)' : 'var(--neg)',
            marginRight: 2,
            fontSize: 9,
          }}
        >
          {natureGlyph}
        </span>
      )}
      {final}
    </span>
  );

  // MIN/MAX chip — sets the value to either bound. Match the in-game
  // Champions training UI which has MIN / MAX shortcuts beside the gauge.
  const chip = (text: string, value: number, title: string) => (
    <button
      type="button"
      onClick={() => onChange(value)}
      className="caps mono"
      style={{
        fontSize: 9,
        padding: '4px 7px',
        background: 'var(--bg-2)',
        color: 'var(--fg-3)',
        border: '1px solid var(--line-1)',
        borderRadius: 2,
        letterSpacing: '0.06em',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        minWidth: 30,
        // 30px width × 28px row keeps each button comfortably above iOS's
        // 44pt accessibility floor when wrapped in the touch-friendly row.
      }}
      title={title}
    >
      {text}
    </button>
  );

  // A visible-thumb slider — bigger touch target on mobile. The bar
  // shows progress; the thumb is a small circle the user can clearly see
  // they're meant to drag.
  const slider = (
    <div style={{ position: 'relative', height: compact ? 28 : 12, flex: 1 }}>
      <div
        style={{
          position: 'absolute',
          top: compact ? 12 : 5,
          left: 0,
          right: 0,
          height: compact ? 4 : 2,
          background: 'var(--bg-3)',
          borderRadius: 2,
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: compact ? 12 : 5,
          left: 0,
          height: compact ? 4 : 2,
          width: `${(ev / perStatCap) * 100}%`,
          background: 'var(--accent-1)',
          borderRadius: 2,
        }}
      />
      {compact && (
        // Visible draggable thumb. Pure-CSS — the actual handler is the
        // invisible <input type="range"> overlay; this just communicates
        // "you can drag this."
        <div
          style={{
            position: 'absolute',
            top: 6,
            left: `calc(${(ev / perStatCap) * 100}% - 8px)`,
            width: 16,
            height: 16,
            borderRadius: '50%',
            background: 'var(--accent-1)',
            border: '2px solid var(--bg-1)',
            pointerEvents: 'none',
          }}
        />
      )}
      <input
        type="range"
        min={0}
        max={perStatCap}
        step={step}
        value={ev}
        onChange={(e) => onChange(Number.parseInt(e.target.value, 10))}
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0,
          cursor: 'pointer',
          width: '100%',
          margin: 0,
        }}
      />
    </div>
  );

  // Unified layout (mobile + desktop). Two visual lines per stat:
  //   line 1: label · spacer · number input · final stat
  //   line 2: MIN · slider w/ visible thumb · MAX
  // The MIN/MAX chips + draggable thumb match the in-game Champions
  // training UI and give desktop the same affordances as mobile. Vertical
  // cost on desktop is ~12 extra rows (2 lines × 6 stats); acceptable
  // for the clarity gain and the elimination of two divergent layouts.
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        padding: '6px 0',
        borderBottom: '1px solid var(--line-1)',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: compact ? '36px 1fr auto 60px' : '36px 32px 1fr auto 60px',
          gap: 8,
          alignItems: 'center',
        }}
      >
        {labelEl}
        {!compact && (
          <span
            className="mono tabular"
            style={{ fontSize: 10, color: 'var(--fg-4)', textAlign: 'right' }}
            title="Base stat"
          >
            {base}
          </span>
        )}
        <span /> {/* spacer */}
        {numberInput}
        {finalEl}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {chip('MIN', 0, `Set ${label} to 0`)}
        {slider}
        {chip('MAX', perStatCap, `Set ${label} to ${perStatCap}`)}
      </div>
    </div>
  );
}

function NatureRow({ nature }: { nature: string }) {
  const up =
    natureGlyph(nature, 'atk') === '▲'
      ? 'atk'
      : natureGlyph(nature, 'def') === '▲'
        ? 'def'
        : natureGlyph(nature, 'spa') === '▲'
          ? 'spa'
          : natureGlyph(nature, 'spd') === '▲'
            ? 'spd'
            : natureGlyph(nature, 'spe') === '▲'
              ? 'spe'
              : null;
  const down =
    natureGlyph(nature, 'atk') === '▼'
      ? 'atk'
      : natureGlyph(nature, 'def') === '▼'
        ? 'def'
        : natureGlyph(nature, 'spa') === '▼'
          ? 'spa'
          : natureGlyph(nature, 'spd') === '▼'
            ? 'spd'
            : natureGlyph(nature, 'spe') === '▼'
              ? 'spe'
              : null;
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span>{nature}</span>
      {up && down && (
        <span
          className="mono caps"
          style={{ fontSize: 8, color: 'var(--fg-4)', letterSpacing: '0.04em' }}
        >
          <span style={{ color: 'var(--pos)' }}>▲</span>
          {up} <span style={{ color: 'var(--neg)' }}>▼</span>
          {down}
        </span>
      )}
    </span>
  );
}
